<script setup lang="ts">
import { CmeProviders } from "~/enums/cme-providers.enums";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const props = defineProps<{
  visible: boolean;
}>();

const { course } = useCourseState();

const showDialog = ref<boolean>(props.visible);
const accreditationBody = ref<string>("");
const showMoreInfo = ref();
const { t } = useI18n();

const options = {
  accreditationBody: [
    { value: 'eaccme', label: t('course.claimCertificate.form.accreditationBody.eaccme') },
    { value: 'accme', label: t('course.claimCertificate.form.accreditationBody.accme')
      .replace(t('course.claimCertificate.form.cmeType.moreInfo.ACCME.title'), '<i>' + t('course.claimCertificate.form.cmeType.moreInfo.ACCME.title') + '</i>') },
  ],
};

const toggle = (event: Event) => {
  showMoreInfo.value.toggle(event);
};

watch(() => props.visible, () => {
  showDialog.value = props.visible;
});

defineEmits<{
  (e: 'dismiss'): void;
  (e: 'downloaded'): void;
}>();
</script>

<template>
  <Dialog
    v-model:visible="showDialog"
    :draggable="false"
    modal
    :header="t('course.claimCertificate.header')"
    class="!w-[100vw] !h-[100svh]"
    :pt="{
      content: {
        class: [
          'py-6 px-4 no-scrollbar rounded-none',
          'w-full h-full max-w-[100vw] max-h-[calc(100svh-65px)]',
          'flex justify-center'
        ]
      },
      header: {
        class: '!rounded-none flex justify-center'
      },
      title: {
        class: 'max-w-[calc(800px-32px)] w-full'
      }
    }"
    @hide="$emit('dismiss')"
  >
    <div class="max-w-[800px] w-full">
      <div class="flex flex-col gap-4 mt-4 mb-12">
        <div class="flex flex-col">
          <div class="flex items-center justify-between">
            <FormFieldLabel for="accreditationBody">
              {{t('course.claimCertificate.form.cmeType.title')}}
            </FormFieldLabel>

            <div
              class="flex items-center gap-1 text-blue-60 cursor-pointer"
              @click="toggle"
            >
              <span class="text-14 font-bold sm:block hidden">{{t('course.claimCertificate.form.cmeType.moreInfo.title')}}</span>
              <FontAwesomeIcon
                icon="fa-circle-info"
                class="!w-4 !h-4"
              />
            </div>
            <OverlayPanel ref="showMoreInfo">
              <div class="max-w-[300px]">
                <div class="font-bold mb-2">
                  {{t('course.claimCertificate.form.cmeType.moreInfo.EACCME.title')}}
                </div>
                <div class=" text-14 text-neutral-60">
                  {{t('course.claimCertificate.form.cmeType.moreInfo.EACCME.description')}}
                </div>

                <Divider />

                <div class="font-bold mb-2 italic">
                  {{t('course.claimCertificate.form.cmeType.moreInfo.ACCME.title')}}
                </div>
                <div class="text-14 text-neutral-60">
                  {{t('course.claimCertificate.form.cmeType.moreInfo.ACCME.description')}}
                </div>
              </div>
            </OverlayPanel>
          </div>

          <Dropdown
            id="accreditationBody"
            v-model="accreditationBody"
            :placeholder="t('global.dropdown.placeholder')"
            option-label="label"
            option-value="value"
            :options="options.accreditationBody"
            aria-describedby="accreditationBody-help"
          >
            <template #option="slotProps">
              <span v-sanitize-html="slotProps.option.label" />
            </template>
            <template #value="{value}">
              <span
                v-if="value"
                v-sanitize-html="options.accreditationBody.find(opt => opt.value === value)?.label"
              />
            </template>
          </Dropdown>
        </div>
      </div>

      <CourseClaimCertificateDialogFormEACCME
        v-if="accreditationBody === 'eaccme'"
        @downloaded="$emit('downloaded')"
      />

      <CourseClaimCertificateDialogFormCMEConsultants
        v-else-if="accreditationBody === 'accme' && course?.cmeProviderSlug === CmeProviders.CmeConsultants"
        @downloaded="$emit('downloaded')"
      />

      <CourseClaimCertificateDialogFormCMEOutfitters
        v-else-if="accreditationBody === 'accme' && course?.cmeProviderSlug === CmeProviders.CmeOutfitters"
        @downloaded="$emit('downloaded')"
      />
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
@import "@mui/assets/styles/utility.scss";

.country-flag-icon {
  &--value {
    height: 0.7rem !important;
  }
  &--option {
    height: 0.8rem !important;
  }
}

.p-dropdown-panel {
  width: 100% !important;
}
</style>